import React, {useEffect} from 'react';
import {Label, Text} from 'react-aria-components';
import {LuCheckSquare, LuClock, LuCog, LuLock, LuSquare} from "react-icons/lu";
import {Navigate} from "react-router-dom";

import FullWidthLayout from "../../layouts/FullWidthLayout/FullWidthLayout.js";
import {LabeledSection, LabeledTextField} from "../../components/Fields/LabeledTextField.jsx";
import DefaultButton, {SecondaryButton} from "../../components/Buttons/DefaultButton/DefaultButton.js";
import api from "../../utils/api.js";
import "./DatabaseCreate.css";
import "./InfoPopups/DatabaseHostingModeInfo.css"
import DefaultMultiDropButton from "../../components/Buttons/DefaultMultiDropButton/DefaultMultiDropButton.js";
import {toast} from "react-toastify";
import DefaultTextListEditor from "../../components/TextListEditor/DefaultTextListEditor.js";
import Toggle from "../../components/Toggle/Toggle.js";
import LoadingLayout from "../../layouts/LoadingLayout/LoadingLayout.js";
import {DatabaseHostingInfoButton} from "./InfoPopups/DatabaseHostingInfoButton.js";
import {VisibleSchemasDisabledInfoButton} from "./InfoPopups/VisibleSchemasDisabledInfoButton.js";
import {RolesWithAccessInfoButton} from "./InfoPopups/RolesWithAccessInfoButton.js";


function SchemaChangeOption(props: { name: string, description: string, onClick: any, selected?: boolean }) {

    return <div className={"schema-change-option-row"+ (props.selected ? " selected" : "")}>
        <div className="selection-box" onClick={props.onClick}>
            {props.selected ? <LuCheckSquare /> : <LuSquare />}
        </div>
        <div className="text">
            <h4 className="schema-change-option-name">{props.name}</h4>
            <div className="schema-change-option-description">{props.description}</div>
        </div>
    </div>
}

function DatabaseCreate({
    returnTo,
    databaseId,
    defaultName,
    defaultDescription,
    lockedInHostingMode,
    defaultSelectedRoleNames,
    defaultSchemaOption,
    defaultPageTitle,
}: {
    returnTo: string;
    databaseId: string;
    defaultName: string;
    defaultDescription: string;
    lockedInHostingMode: boolean;
    defaultSelectedRoleNames: string[];
    defaultSchemaOption: string;
    defaultPageTitle: string;
}) {
    const [submitting, setSubmitting] = React.useState(false)
    const [name, setName] = React.useState(defaultName)
    const [description, setDescription] = React.useState(defaultDescription)
    const [selectedOption, setSelectedOption] = React.useState(defaultSchemaOption)
    const [redirectTo, setRedirectTo] = React.useState(null)
    const [hostingMode, setHostingMode] = React.useState("Cloud-Hosted")
    const [roles, setRoles] = React.useState([])
    const [databaseUri, setDatabaseUri] = React.useState("")
    const [tables, setTables] = React.useState([])
    const [roleNames, setRoleNames] = React.useState(roles.map(role => role.name))
    const [selectedRoleNames, setSelectedRoleNames] = React.useState(defaultSelectedRoleNames ? defaultSelectedRoleNames : [])
    const [enableSchemaAllowList, setEnableSchemaAllowList] = React.useState(false)
    const [selectedSchemas, setSelectedSchemas] = React.useState([])
    const [availableSchemas, setAvailableSchemas] = React.useState([])
    let id = databaseId;
    
    const CLOUD_HOSTED = "Cloud-Hosted"
    const SELF_HOSTED = "Self-Hosted"

    async function submit() {
        setSubmitting(true)

        const selectedRoleIds = []
        for (const name of selectedRoleNames) {
            for (const role of roles) {
                if (role.name === name) {
                    selectedRoleIds.push(role.id)
                }
            }
        }

        let databaseConnection = null;
        if (hostingMode === "Cloud-Hosted") {
            const parsed = URL.parse(databaseUri)
            if (parsed === null) {
                toast.warning("Invalid Database URI")
                setSubmitting(false)
                return
            }

            databaseConnection = {
                'username': parsed.username,
                'password': parsed.password,
                'host': parsed.hostname,
                'port': parsed.port ? parsed.port : "5432",
                'database_name': parsed.pathname.slice(1),
            }
        }

        if (id!==null) {
            api.updateDatabase(
                id,
                {
                    name: name,
                    description: description,
                    on_schema_change: selectedOption,
                    role_ids: selectedRoleIds,
                    hosting_mode: hostingMode,
                    database_connection: databaseConnection,
                    schema_allow_list: enableSchemaAllowList ? selectedSchemas : null,
                }
            ).then((result) => {
                if (result) {
                    setRedirectTo(`/database/${id}`)
                    setSubmitting(false)
                } else {
                    setRedirectTo(`/database/${id}/general`)
                    setSubmitting(false)
                }
            })
        } else {
            const hostingModeStr = hostingMode === SELF_HOSTED ? "self-hosted" : "cloud"
            await api.createDatabase({
                name: name,
                role_ids: selectedRoleIds,
                hosting_mode: hostingModeStr,
                database_connection: databaseConnection,
                schema_allow_list: enableSchemaAllowList ? selectedSchemas : null,
            }).then((response) => {
                id = response.id
                setRedirectTo(`/database/${id}`)
            }).catch(e => {
                returnTo = "/database/create"
            })
            setSubmitting(false)
        }
    }

    useEffect(() => {
        if (id!==null) {
            api.getDatabase(databaseId).then(server => {
                setHostingMode(server.hostingMode)
                setTables(server.tables)
                if (server.hostingMode === "self-hosted") {
                    setDatabaseUri(server.databaseUri)
                } else {
                    setDatabaseUri("")
                }
            })
            api.listRoles().then(data => {
                setRoles(data)
                setRoleNames(data.map(role => role.name))
                api.getDatabase(id).then(server => {
                    setName(server.name)
                    setDescription(server.description)
                    setSelectedOption(server.onSchemaChange)
                    setSelectedRoleNames(server.roles.map(role => role.name))
                    setEnableSchemaAllowList(server.allowedSchemasEnabled && server.tables.length > 0)
                    setSelectedSchemas(server.allowedSchemas? server.allowedSchemas : [])

                    const newAvailableSchemas =  []
                    server.tables.forEach((table) => {
                        if (!newAvailableSchemas.includes(table.tableSchema)) {
                            newAvailableSchemas.push(table.tableSchema)
                        }
                    })
                    if (!newAvailableSchemas.includes("public")) {
                        newAvailableSchemas.push("public")
                    }
                    if (!newAvailableSchemas.includes("pg_catalog")) {
                        newAvailableSchemas.push("pg_catalog")
                    }
                    if (!newAvailableSchemas.includes("information_schema")) {
                        newAvailableSchemas.push("information_schema")
                    }

                    setAvailableSchemas(newAvailableSchemas)

                })
            })
        }
        api.listRoles().then(data => {
            setRoles(data)
            setRoleNames(data.map(role => role.name))
        })
    }, []);

    if (redirectTo !== null && redirectTo !== window.location.pathname) {
        return <Navigate to={redirectTo} />
    }

    if (submitting) {
        return <LoadingLayout />
    }

    let hostingModeComponent = <div className="hosting-mode">
        <Toggle
            label="Hosting Mode"
            info={<DatabaseHostingInfoButton/>}
            choices={[CLOUD_HOSTED, SELF_HOSTED]}
            selected={hostingMode}
            onSelect={(option) => {
                setHostingMode(option)
            }}
        />
    </div>

    if (lockedInHostingMode !== undefined) {
        hostingModeComponent = <div className="hosting-mode">
            <div className="locked-in-hosting-mode">
                <Label className="key">Hosting Mode</Label>
                <div className="value">{ lockedInHostingMode }</div>
                <div className="lock"><LuLock/></div>
            </div>
        </div>
    }


    let cloudHostingDBURIComponent = null;
    if (hostingMode === "Cloud-Hosted") {
        cloudHostingDBURIComponent = <div className="cloud-hosting-db-uri">
            <LabeledTextField
                label="Database URI"
                value={databaseUri}
                placeholder="i.e. postgres://user:password@host:port/dbname"
                onChange={(e) => {
                    setDatabaseUri(e.target.value)
                }}
                name="cloud-db-uri"
                errorLoc={["body", "cloud-db-uri"]}
                hiddenLabel="database connection string"
                togglableMasking={true}
            />
        </div>
    }

    let allowedSchemaEditor = <DefaultTextListEditor
        onChange={(newList) => {
            setSelectedSchemas(newList)
        }}
        initialValuesList={availableSchemas}
    />
    if (defaultSchemaOption.length > 0) {
        allowedSchemaEditor = <DefaultMultiDropButton
            name="Specified Schemas"
            emptyText="no schemas specified"
            selected={selectedSchemas}
            options={availableSchemas}
            disabled={false}
            label="Specified Schemas"
            onSelect={setSelectedSchemas}
            errorLoc={undefined}
            className="specified-schemas"
            info={<VisibleSchemasDisabledInfoButton/>}
        />
    }

    const content = <div>
        <LabeledSection label="General" icon={<LuCog/>} className="general-settings">
            <div className="name">
                <LabeledTextField
                    label="Name"
                    value={name}
                    placeholder="Database Name"
                    onChange={(e)=> {
                        setName(e.target.value)
                    }}
                    name="name"
                    errorLoc={["body", "name"]}
                    hiddenLabel={undefined}
                />
            </div>

            {hostingModeComponent}
            {cloudHostingDBURIComponent}


            <div className="permitted-roles">
                <DefaultMultiDropButton
                    name="Roles with Access"
                    emptyText="inaccessible to all users"
                    selected={selectedRoleNames}
                    options={roleNames}
                    disabled={false}
                    label="Roles with Access"
                    onSelect={setSelectedRoleNames}
                    errorLoc={undefined} className={undefined}
                    info={<RolesWithAccessInfoButton/>}
                />
            </div>
            <div className="allowed-schemas">
                <Toggle
                    choices={["All", "Specified"]}
                    selected={enableSchemaAllowList ? "Specified" : "All"}
                    onSelect={(option) => {
                        if (option === "All") {
                            setEnableSchemaAllowList(false);
                        } else {
                            setEnableSchemaAllowList(true);
                        }
                    }}
                    label="Visible Schemas"
                    info={<VisibleSchemasDisabledInfoButton/>}
                    disabled={!lockedInHostingMode || tables.length === 0}
                />
                {enableSchemaAllowList ? allowedSchemaEditor : null}
            </div>

        </LabeledSection>

        <LabeledSection label="Schema Change" icon={<LuClock/>} className="schema-change-options">
            <div className="top-text">
                <Text>If the source database has a column added, what should the proxy allow the users to
                    see?</Text>
            </div>
            <SchemaChangeOption
                name="Hide Column"
                description="This will hide the column from the user. The user will not even be aware that the column exists."
                onClick={() => {
                    setSelectedOption("Hide Column")
                }}
                selected={selectedOption === "Hide Column"}
            />
            <SchemaChangeOption
                name="Show column but redact values"
                description="The column header will be visible and results from the column will be returned in queries but the values will be redacted (* in text columns, -1 in number columns)."
                onClick={() => {
                    setSelectedOption("Redact All")
                }}
                selected={selectedOption === "Redact All"}
            />
            <SchemaChangeOption
                name="Show column with true values"
                description="The column header will be visible and all results will be returned."
                onClick={() => {
                    setSelectedOption("Show Column")
                }}
                selected={selectedOption === "Show Column"}
            />
        </LabeledSection>

        <div className="save-row">
            <SecondaryButton
                className="cancel-button"
                text="Cancel"
                href={id===null? "/databases" : `/database/${id}`} />
            <DefaultButton text="Save" onPress={submit} oneClick />
        </div>
    </div>

    return <div className="database-create">
        <FullWidthLayout
            title={defaultPageTitle}
            mainContent={content}
        />
    </div>
}

export default DatabaseCreate;
